import { Search, X } from 'lucide-react'

import type { SavingsAccountStatus, SavingsProductClass } from '@/api/savings-types'
import type { Branch } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import {
  SAVINGS_ACCOUNT_STATUSES,
  SAVINGS_PRODUCT_CLASSES,
  productClassLabel,
  savingsAccountStatusLabel,
} from './constants'

const ANY = 'any'

/** `ListSavingsAccountsValidator` refuses anything longer with `savings.search_too_long`. */
export const SEARCH_MAX_LENGTH = 32

/**
 * What narrows the account list.
 *
 * Everything here writes into the URL rather than into component state, so a filtered list is a
 * place: "the dormant security deposits at Kaneshie" is a link somebody can send, and pressing
 * Back out of an account lands on the page of the list it was opened from.
 */
export function SavingsAccountFilters({
  searchText,
  onSearchTextChange,
  status,
  onStatusChange,
  productClass,
  onProductClassChange,
  branchId,
  onBranchChange,
  branches,
  hasActiveFilters,
  onClearFilters,
}: {
  searchText: string
  onSearchTextChange: (value: string) => void
  status: SavingsAccountStatus | undefined
  onStatusChange: (status: SavingsAccountStatus | undefined) => void
  productClass: SavingsProductClass | undefined
  onProductClassChange: (productClass: SavingsProductClass | undefined) => void
  branchId: string | undefined
  onBranchChange: (branchId: string | undefined) => void
  branches: Branch[]
  hasActiveFilters: boolean
  onClearFilters: () => void
}) {
  return (
    <div className="flex flex-col gap-3 rounded-card border border-border-subtle bg-surface p-3 lg:flex-row lg:items-end">
      <div className="min-w-56 flex-1 space-y-1.5">
        <Label htmlFor="savings-search">Search</Label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
            aria-hidden
          />
          <Input
            id="savings-search"
            value={searchText}
            autoComplete="off"
            spellCheck={false}
            placeholder="Account number, customer number, or name"
            className="pl-9"
            // The server refuses a longer term outright with `savings.search_too_long`, so the box
            // stops at the limit rather than letting a full name become a 400.
            maxLength={SEARCH_MAX_LENGTH}
            onChange={(event) => onSearchTextChange(event.target.value)}
          />
        </div>
      </div>

      <div className="min-w-40 space-y-1.5">
        <Label htmlFor="savings-status">Status</Label>
        <Select
          value={status ?? ANY}
          onValueChange={(next) =>
            onStatusChange(next === ANY ? undefined : (next as SavingsAccountStatus))
          }
        >
          <SelectTrigger id="savings-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any status</SelectItem>
            {SAVINGS_ACCOUNT_STATUSES.map((candidate) => (
              <SelectItem key={candidate} value={candidate}>
                {savingsAccountStatusLabel(candidate)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="min-w-44 space-y-1.5">
        <Label htmlFor="savings-class">Class</Label>
        <Select
          value={productClass ?? ANY}
          onValueChange={(next) =>
            onProductClassChange(next === ANY ? undefined : (next as SavingsProductClass))
          }
        >
          <SelectTrigger id="savings-class">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Both classes</SelectItem>
            {SAVINGS_PRODUCT_CLASSES.map((candidate) => (
              <SelectItem key={candidate} value={candidate}>
                {productClassLabel(candidate)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {branches.length > 1 ? (
        <div className="min-w-44 space-y-1.5">
          <Label htmlFor="savings-branch">Branch</Label>
          <Select
            value={branchId ?? ANY}
            onValueChange={(next) => onBranchChange(next === ANY ? undefined : next)}
          >
            <SelectTrigger id="savings-branch">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Every branch I can see</SelectItem>
              {branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branch.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {hasActiveFilters ? (
        <Button variant="ghost" onClick={onClearFilters}>
          <X />
          Clear
        </Button>
      ) : null}
    </div>
  )
}
